import React, { useCallback, useEffect, useState } from 'react';
import { listRetakePaymentsApi, exportRetakePaymentsCsvUrl, type RetakePayment } from '../services/retakePaymentApi';
import './RetakePaymentPage.scss';

type Filters = {
  tuNgay: string;
  denNgay: string;
  trangThai: string;
};

const PAGE_SIZE = 50;

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('vi-VN') : '—';

const fmtAmount = (v: string | number) =>
  Number(v).toLocaleString('vi-VN') + ' đ';

const RetakePaymentPage: React.FC = () => {
  const [filters, setFilters] = useState<Filters>({ tuNgay: '', denNgay: '', trangThai: '' });
  const [applied, setApplied] = useState<Filters>(filters);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<RetakePayment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: Filters, p: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await listRetakePaymentsApi({ ...f, page: p, limit: PAGE_SIZE });
      setRows(result.rows);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(applied, page);
  }, [load, applied, page]);

  const handleSearch = () => {
    setApplied(filters);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="rp">
      <h2 className="rp__title">Thanh toán thi lại</h2>

      <div className="rp__filters">
        <div className="rp__filter-group">
          <label>Từ ngày</label>
          <input
            type="date"
            value={filters.tuNgay}
            onChange={e => setFilters(f => ({ ...f, tuNgay: e.target.value }))}
          />
        </div>
        <div className="rp__filter-group">
          <label>Đến ngày</label>
          <input
            type="date"
            value={filters.denNgay}
            onChange={e => setFilters(f => ({ ...f, denNgay: e.target.value }))}
          />
        </div>
        <div className="rp__filter-group">
          <label>Trạng thái</label>
          <select
            value={filters.trangThai}
            onChange={e => setFilters(f => ({ ...f, trangThai: e.target.value }))}
          >
            <option value="">Tất cả</option>
            <option value="matched">Đã khớp</option>
            <option value="unmatched">Chưa khớp</option>
          </select>
        </div>
        <div className="rp__actions">
          <button className="rp__btn rp__btn--primary" onClick={handleSearch}>
            Tìm kiếm
          </button>
          <a
            className="rp__btn rp__btn--export"
            href={exportRetakePaymentsCsvUrl(applied)}
            download="thanh-toan-thi-lai.csv"
          >
            <i className="material-icons" style={{ fontSize: '1rem' }}>download</i>
            Xuất CSV
          </a>
        </div>
      </div>

      <div className="rp__summary">
        {loading ? 'Đang tải…' : `${total} giao dịch`}
        {error && <span style={{ color: 'red', marginLeft: 12 }}>{error}</span>}
      </div>

      <div className="rp__table-wrap">
        <table className="rp__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Ngày GD</th>
              <th>Số tiền</th>
              <th>Nội dung CK</th>
              <th>CCCD</th>
              <th>Mã kỳ SH</th>
              <th>Họ tên</th>
              <th>Ngân hàng</th>
              <th>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="rp__empty">Không có dữ liệu</td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.id}>
                <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmt(r.ngayGD)}</td>
                <td className="rp__amount">{fmtAmount(r.soTien)}</td>
                <td><div className="rp__content" title={r.noiDung ?? ''}>{r.noiDung || '—'}</div></td>
                <td>{r.cccdMatched || '—'}</td>
                <td>{r.maKySHMatched || '—'}</td>
                <td>{r.hoTenMatched || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {r.bankAccount ? `${r.bankAccount.bankName} ${r.bankAccount.accountNumber}` : '—'}
                </td>
                <td>
                  <span className={`rp__badge rp__badge--${r.trangThai}`}>
                    {r.trangThai === 'matched' ? 'Đã khớp' : 'Chưa khớp'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="rp__pagination">
          <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}>‹ Trước</button>
          <span>Trang {page} / {totalPages}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>Sau ›</button>
        </div>
      )}
    </div>
  );
};

export default RetakePaymentPage;
